import { GetUserDetailsResponseProto } from "@noom/noom-contracts/noom_contracts/backend/user/user_service";
import {
  SexProto_Value,
  GenderProto_Value,
} from "@noom/noom-contracts/noom_contracts/backend/user/model/sex_and_gender";
import { SurveyAnswersState } from "./redux/slices/surveyAnswers";
import { genderConstants, sexConstants } from "./sexAndGender";
import { convertKgToLb, convertCmToFt } from "src/primitive/UnitUtils";
import { calculateAge } from "src/utils/datetime";
import { shouldUseImperialUnits } from "./userSegment/features";
import Unit from "./Unit";

/**
 * Maps proto gender enum to internal gender constants
 * @param protoGender - Proto gender enum value
 * @returns Internal gender constant or undefined
 */
function mapProtoGenderToInternal(
  protoGender: GenderProto_Value
): genderConstants | undefined {
  switch (protoGender) {
    case "MALE":
      return genderConstants.male;
    case "FEMALE":
      return genderConstants.female;
    case "SELF_ID":
      return genderConstants.nonBinary;
    // An absent gender is not the same answer as declining to give one, and
    // callers overwrite existing answers with whatever this returns.
    case "UNKNOWN":
    default:
      return undefined;
  }
}

/**
 * Maps proto sex enum to internal sex constants
 * @param protoSex - Proto sex enum value
 * @returns Internal sex constant or undefined
 */
function mapProtoSexToInternal(
  protoSex: SexProto_Value
): sexConstants | undefined {
  switch (protoSex) {
    case "MALE":
      return sexConstants.male;
    case "FEMALE":
      return sexConstants.female;
    case "INTERSEX":
      return sexConstants.intersex;
    case "UNKNOWN":
    case "UNSPECIFIED":
    default:
      return undefined;
  }
}

/**
 * Transforms the getUserDetails API response into SurveyAnswersState updates
 * @param response - The API response
 * @returns The survey answers the response describes
 */
export function transformUserDetailsToSurveyAnswers(
  response: GetUserDetailsResponseProto
): Partial<SurveyAnswersState> {
  const surveyAnswers: Partial<SurveyAnswersState> = {};
  const isImperial = shouldUseImperialUnits();

  if (response.weightInKg > 0) {
    const weightLb = convertKgToLb({
      mainUnitValue: response.weightInKg,
    }).mainUnitValue;
    surveyAnswers.weightKg = response.weightInKg;
    surveyAnswers.weightImp = weightLb;
    surveyAnswers.weightUnit = isImperial ? Unit.POUND.id : Unit.KILOGRAM.id;
    if (isImperial) {
      surveyAnswers.weight = Math.round(weightLb);
    }
  }

  // Convert and set height in surveyAnswers
  if (response.heightInCm > 0) {
    surveyAnswers.heightCm = response.heightInCm;
    surveyAnswers.heightUnit = isImperial ? Unit.FEET.id : Unit.CENTIMETER.id;
    if (isImperial) {
      const { mainUnitValue: heightFeet, secondaryUnitValue: heightInch } =
        convertCmToFt({ mainUnitValue: response.heightInCm });
      surveyAnswers.heightFeet = heightFeet;
      surveyAnswers.heightInch = heightInch || 0;
    }
  }

  // Map and set gender in surveyAnswers
  if (response.gender !== undefined) {
    const internalGender = mapProtoGenderToInternal(response.gender);
    if (internalGender !== undefined) {
      surveyAnswers.gender = [internalGender];
    }
  }

  // Map and set sex in surveyAnswers
  if (response.sex !== undefined) {
    const internalSex = mapProtoSexToInternal(response.sex);
    if (internalSex !== undefined) {
      surveyAnswers.sex = [internalSex];
    }
  }

  // Set age in surveyAnswers - prefer calculated age from birth date, fallback to approximate age
  if (
    response.birthDate &&
    response.birthDate.year &&
    response.birthDate.month &&
    response.birthDate.day
  ) {
    const birthDateObj = new Date(
      response.birthDate.year,
      response.birthDate.month - 1,
      response.birthDate.day
    );
    surveyAnswers.ageYears = calculateAge(birthDateObj);
  } else if (response.approximateAge > 0) {
    surveyAnswers.ageYears = response.approximateAge;
  }

  // Convert and set target weight in surveyAnswers
  if (response.targetWeightInKg > 0) {
    surveyAnswers.idealWeightKg = response.targetWeightInKg;
    if (isImperial) {
      surveyAnswers.idealWeight = Math.round(
        convertKgToLb({ mainUnitValue: response.targetWeightInKg })
          .mainUnitValue
      );
    }
  }

  return surveyAnswers;
}
